"use client"

/**
 * PopunderInjector
 * Loads the popunder script from the configured ad network.
 * Supports two formats:
 *   1. Plain src URL (e.g. ClickAdilla legacy, TrafficStars)
 *   2. src URL + data-admpid attribute (ClickAdilla wpadmngr format)
 *
 * - Only loads if admin has enabled it
 * - Skips for paid subscribers and staff
 * - Waits until the session is delay_minutes old (default 3) before it will
 *   even start listening -- then fires on the first qualifying click after
 *   that point (not every click, and not the literal first click of a
 *   brand-new session)
 */

import { useEffect, useRef } from "react"
import { isAdFreePath } from "@/lib/ad-placement"
import { useAuthStore } from "@/lib/auth-store"
import { isAdCooldownExpired, markAdShown, isAdTestModeOn, isSessionOldEnoughForAds, getSessionAgeMs } from "@/lib/ad-cooldown"
import { hasPremiumAccess } from "@/lib/entitlements"

// "supporter" was previously missing here — Supporter's advertised "No ads" perk (see
// app/subscribe/page.tsx TIERS) wasn't actually being enforced. Added alongside "goon".
const PREMIUM_TIERS = ["goontier_1", "goontier_2", "goontier_basic", "goontier_pro", "supporter", "goon", "goon_plus", "goon_max", "founder"]
const SKIP_ROLES = ["admin", "super_admin", "moderator"]

export function PopunderInjector() {
  const { profile, isLoading } = useAuthStore()
  const role = profile?.role
  const subscriptionTier = (profile as any)?.subscription_tier
  const injected = useRef(false)
  const delayTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (injected.current) return
    if (isLoading) return

    // "Force Ads" test mode (Admin → /ads/preview) deliberately overrides the
    // premium and staff exclusions here. Without it a popunder is untestable
    // by the only people who can configure it: staff roles are skipped AND
    // every staff account is premium, so an owner verifying their own setup
    // would always be excluded twice over and see nothing — indistinguishable
    // from a broken integration. The flag is localStorage-scoped to one
    // browser and set from an admin-only page, so it can't leak ads to real
    // premium users. Cooldown is likewise bypassed so repeat tests work.
    const testMode = isAdTestModeOn()

    if (!testMode) {
      // hasPremiumAccess covers paid tiers, founders, lifetime buyers and staff —
      // the old PREMIUM_TIERS check missed anyone who paid without an active
      // Stripe subscription. SKIP_ROLES stays: it's a separate policy (roles that
      // skip popunders for reasons unrelated to payment).
      if (hasPremiumAccess(profile as any)) return
      if (role && SKIP_ROLES.includes(role)) return
    }

    fetch("/api/admin/popunder-config")
      .then((r) => r.json())
      .then((config) => {
        // Test mode also overrides the global enable toggle, so popunder can
        // be verified end-to-end WITHOUT switching it on for real visitors —
        // otherwise the only way to test would be to ship it live first.
        if (!testMode && !config.enabled) return

        // Mobile popunder is a real, separate ExoClick zone type — different
        // pop mechanics than desktop window.open(), not just a resize of the
        // same creative. Coarse pointer + narrow viewport, same signal the
        // rest of the ad system already uses for tier/layout decisions.
        const isMobile = typeof window !== "undefined" &&
          (window.matchMedia?.("(max-width: 767px)").matches || window.matchMedia?.("(pointer: coarse)").matches)
        const t1 = isMobile ? config.embed_code_mobile_tier1 : config.embed_code_tier1
        const t2 = isMobile ? config.embed_code_mobile_tier2 : config.embed_code_tier2
        const t3 = isMobile ? config.embed_code_mobile_tier3 : config.embed_code_tier3

        // Pick the zone matching the max_tier setting, falling back through
        // tiers if a specific one isn't configured — same fallback order as
        // every other placement's tier resolution.
        const tier = config.max_tier ?? 3
        const embedCode: string =
          tier === 1 ? (t1 || t2 || t3) :
          tier === 2 ? (t2 || t1 || t3) :
                       (t3 || t2 || t1)

        if (!embedCode) return
        if (!testMode && !isAdCooldownExpired("popunder", config.cooldown_minutes ?? 0)) return

        // Firing on the literal first click of a brand-new session reads as
        // hostile. arm() (mark-as-shown + actually wire up the trigger) is
        // deferred until the session has been open delay_minutes -- NOT
        // marked as shown / locked via injected.current until that point,
        // so a visitor who leaves before the delay elapses correctly still
        // gets a popunder next session instead of the cooldown silently
        // consuming an attempt that never actually happened.
        const arm = () => {
          injected.current = true
          markAdShown("popunder")
          // Goonpals, commissions and settings never get an ad. A click in
          // one of those does not use up the trigger: it stays armed for the
          // next click somewhere else.
          const load = () => {
            if (isAdFreePath(window.location.pathname)) return
            document.removeEventListener("click", load, true)
            loadScript(embedCode)
          }
          if (config.trigger === "page_load") {
            load()
          } else {
            document.addEventListener("click", load, { capture: true })
          }
        }

        const delayMinutes = testMode ? 0 : (config.delay_minutes ?? 3)
        if (isSessionOldEnoughForAds(delayMinutes)) {
          arm()
        } else {
          const remainingMs = Math.max(0, delayMinutes * 60000 - getSessionAgeMs())
          delayTimerRef.current = setTimeout(arm, remainingMs)
        }
      })
      .catch(() => {})

    return () => {
      if (delayTimerRef.current) clearTimeout(delayTimerRef.current)
    }
  }, [isLoading, role, subscriptionTier])

  return null
}

/**
 * Loads the network's popunder script inside a hidden, sandboxed iframe
 * rather than directly into our own document.
 *
 * Injecting it into the page head (the previous approach) gives a
 * third-party script full top-level navigation rights, and it used them:
 * confirmed live, the first click after load navigated the entire tab away
 * from Goonru to an external site. That is a redirect, not a popunder — the
 * user loses the app completely instead of finding an ad tab behind it.
 *
 * The sandbox list is the fix, and mirrors the same pattern used by the
 * network-ad iframe in components/ads/InterstitialAd.tsx:
 *   allow-scripts                  — the script has to actually run
 *   allow-popups                   — opening the background tab IS the product
 *   allow-popups-to-escape-sandbox — so the opened tab renders normally
 * and critically OMITS:
 *   allow-top-navigation[-by-user-activation] — cannot move our page
 *   allow-same-origin              — cannot touch our DOM, storage or cookies
 *
 * Net effect: the network can still open its popunder tab, but it can no
 * longer hijack or redirect Goonru itself.
 */
function loadScript(embedCode: string) {
  if (document.querySelector("iframe[data-popunder]")) return

  // Injects the zone's embed code verbatim — whatever shape the network
  // gives it (ClickAdilla's single `<script src data-admpid>` tag, ExoClick's
  // config-vars block plus a separate `<script src>`, or anything else) —
  // instead of reconstructing one specific network's tag shape from a bare
  // ID. See the comment on network-grid-ad's placement resolution and
  // popunder-config's resolveEmbedCodesFromZones for why: that reconstruction
  // only worked for ClickAdilla, so an ExoClick popunder zone silently had no
  // way to actually render here even once its ID was wired up.
  const srcDoc = `<!DOCTYPE html><html><head></head><body>${embedCode}</body></html>`

  const frame = document.createElement("iframe")
  frame.setAttribute("data-popunder", "1")
  frame.setAttribute("sandbox", "allow-scripts allow-popups allow-popups-to-escape-sandbox")
  frame.setAttribute("aria-hidden", "true")
  frame.setAttribute("tabindex", "-1")
  frame.title = "Advertisement"
  // Kept in the layout (not display:none) because some networks refuse to
  // serve into a frame with no box, but visually inert and non-interactive.
  frame.style.cssText =
    "position:fixed;width:1px;height:1px;bottom:0;left:0;opacity:0;border:0;pointer-events:none;z-index:-1"
  frame.srcdoc = srcDoc

  document.body.appendChild(frame)
  armFocusReclaim()
}

/**
 * Reported live: "the popunder took me to the popunder page and didn't keep
 * me on the app page." Not a bug in the iframe/sandbox setup above — modern
 * Chrome/Firefox/Edge simply do not let a script open a tab silently behind
 * the current one anymore. window.open() on a trusted user gesture always
 * switches OS focus to the new tab; no sandbox flag or permission changes
 * that, and every publisher running these same ad networks hits it.
 *
 * The one thing our OWN page (never sandboxed — this is our top-level
 * script, not the network's) can still do is notice it just lost focus and
 * reclaim it once. This is a plain self-refocus, not a cross-origin trick.
 * Deliberately ONE SHOT and bounded: looping this on every `blur` would
 * fight a visitor who alt-tabs or switches tabs on purpose minutes later,
 * which is the exact focus-jacking pattern browsers try to stop. Disarms
 * itself after the first blur, or after FOCUS_RECLAIM_WINDOW_MS with no
 * blur at all (covers the case where the ad script never actually opens
 * anything, e.g. blocked by an ad blocker).
 */
const FOCUS_RECLAIM_WINDOW_MS = 5000
function armFocusReclaim() {
  const reclaim = () => {
    window.removeEventListener("blur", reclaim)
    window.focus()
  }
  window.addEventListener("blur", reclaim)
  setTimeout(() => window.removeEventListener("blur", reclaim), FOCUS_RECLAIM_WINDOW_MS)
}
